import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PATHS, SEARCH_PARAMS } from 'src/constants/navigateRoutes';
import { getSessionDetails } from 'src/services/auth';
import { Box } from '@mui/material';
import CustomLoading from 'src/components/custom-loading';
import styleVariables from '../../scss/_variables.scss';
import { DASHBOARD_UNAUTHORIZED_REASON_CODES } from 'src/constants/dashboardAuth';

interface GuestRouteProps {
  children: ReactNode;
}

const GuestRoute: React.FC<GuestRouteProps> = observer(({ children }) => {
  const [isFetching, setIsFetching] = useState(true);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const fetchUserData = useCallback(async () => {
    const unauthorizedReason = searchParams.get(SEARCH_PARAMS.reason);
    const oauthReauthIntent = searchParams.get(SEARCH_PARAMS.oauthReauthIntent);

    // Account-bound reconnect links must remain usable even if a stale but
    // otherwise valid dashboard cookie exists in this browser.
    if (oauthReauthIntent) {
      setIsFetching(false);

      return;
    }

    if (
      unauthorizedReason ===
      DASHBOARD_UNAUTHORIZED_REASON_CODES.ACCOUNT_DEACTIVATED
    ) {
      setIsFetching(false);

      return;
    }

    setIsFetching(true);
    const {
      success: getSessionInfoSuccess,
      content: sessionInfo,
      errorCode,
    } = await getSessionDetails();

    if (getSessionInfoSuccess && sessionInfo?.isSessionUsable) {
      navigate(PATHS.home, { replace: true });

      return;
    }

    if (
      errorCode === DASHBOARD_UNAUTHORIZED_REASON_CODES.SCOPES_MISSING &&
      unauthorizedReason !== DASHBOARD_UNAUTHORIZED_REASON_CODES.SCOPES_MISSING
    ) {
      const updatedSearchParams = new URLSearchParams(searchParams);

      updatedSearchParams.set(
        SEARCH_PARAMS.reason,
        DASHBOARD_UNAUTHORIZED_REASON_CODES.SCOPES_MISSING,
      );

      navigate(`${PATHS.unAuthorized}?${updatedSearchParams.toString()}`, {
        replace: true,
      });

      return;
    }

    if (
      unauthorizedReason === DASHBOARD_UNAUTHORIZED_REASON_CODES.SCOPES_MISSING
    ) {
      setIsFetching(false);

      return;
    }

    setIsFetching(false);
  }, [navigate, searchParams]);

  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (hasFetchedRef.current) {
      return;
    }

    hasFetchedRef.current = true;
    fetchUserData();
  }, [fetchUserData]);

  if (isFetching) {
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          overflow: 'hidden',
          background: styleVariables.chineseBlack,
        }}
      >
        <CustomLoading />
      </Box>
    );
  }

  // If the user is not authenticated, render the children components
  return <React.Fragment>{children}</React.Fragment>;
});

export default GuestRoute;
